/**
 * PublicDriverPage — Phase 33.
 *
 * Pagina pubblica `/d/:token` con dashboard read-only del driver.
 * Privacy: nessun dato sensibile esposto (no email, phone, indirizzi, IBAN).
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, ShieldCheck, Truck, MapPinned, AlertTriangle } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";

const VEHICLE_LABEL = {
  bike: "Bicicletta",
  ebike: "E-Bike",
  scooter: "Scooter",
  car: "Auto",
  auto: "Auto",
  van: "Furgone",
};

export default function PublicDriverPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/drivers/${token}`);
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err.response?.status === 404 ? "not_found" : "error", data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return <PageShell><div className="card-soft p-10 text-center text-dapnow-muted">Caricamento profilo…</div></PageShell>;
  }
  if (state.error === "not_found") {
    return (
      <PageShell>
        <div className="card-soft p-10 text-center" data-testid="public-driver-not-found">
          <AlertTriangle className="h-6 w-6 mx-auto text-amber-600" />
          <p className="mt-3 font-display text-lg font-semibold tracking-tight">Profilo non disponibile</p>
          <p className="text-sm text-dapnow-muted mt-1 max-w-md mx-auto">
            Il link condiviso non è più valido o il driver ha revocato l'accesso pubblico.
          </p>
          <Link to="/" className="btn-ghost mt-5 inline-flex">Torna al sito</Link>
        </div>
      </PageShell>
    );
  }
  if (state.error) {
    return <PageShell><div className="card-soft p-10 text-center text-dapnow-red">Impossibile caricare il profilo.</div></PageShell>;
  }

  const d = state.data;
  const rating = typeof d.average_rating === "number" ? d.average_rating : null;
  return (
    <PageShell>
      <article className="card-soft p-6 sm:p-8 space-y-6" data-testid="public-driver-card">
        <header className="flex items-center gap-4">
          <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-dapnow-blue text-white font-display text-2xl font-bold shadow-sm">
            {d.initial || "D"}
          </span>
          <div className="min-w-0">
            <p className="overline text-emerald-700">Driver DapNow</p>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tighter truncate" data-testid="public-driver-name">{d.display_name}</h1>
            <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
              {d.is_verified && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 font-semibold" data-testid="public-driver-verified">
                  <ShieldCheck className="h-3 w-3" /> Verificato
                </span>
              )}
              {d.vehicle_type && (
                <span className="inline-flex items-center gap-1 rounded-full bg-dapnow-bg border border-dapnow-line text-dapnow-ink px-2 py-0.5 font-semibold">
                  <Truck className="h-3 w-3" /> {VEHICLE_LABEL[d.vehicle_type] || d.vehicle_type}
                </span>
              )}
            </div>
          </div>
        </header>

        <dl className="grid grid-cols-3 gap-3">
          <Stat label="Consegne" value={d.delivered_count ?? 0} testid="public-driver-deliveries" />
          <Stat
            label="Valutazione"
            value={rating !== null ? <span className="inline-flex items-center gap-1"><Star className="h-4 w-4 text-amber-500" fill="currentColor" />{rating}</span> : "—"}
            sub={d.ratings_count ? `${d.ratings_count} recensioni` : "Nessuna ancora"}
            testid="public-driver-rating"
          />
          <Stat label="Su DapNow da" value={d.since ? new Date(d.since).getFullYear() : "—"} testid="public-driver-since" />
        </dl>

        {Array.isArray(d.commute_routes) && d.commute_routes.length > 0 && (
          <div data-testid="public-driver-commute">
            <p className="overline">Tragitti pendolari abituali</p>
            <ul className="mt-2 space-y-1.5">
              {d.commute_routes.map((r, i) => (
                <li key={i} className="text-sm flex items-center gap-2">
                  <MapPinned className="h-4 w-4 text-dapnow-blue shrink-0" />
                  <span><strong>{r.from_city || "—"}</strong> → <strong>{r.to_city || "—"}</strong></span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-dapnow-muted mt-2">Solo Ticino e Grigioni italiano. Nessuna tratta oltreconfine.</p>
          </div>
        )}

        <div className="border-t border-dapnow-line pt-4 text-center">
          <p className="text-xs text-dapnow-muted leading-relaxed">
            Pagina pubblica condivisa dal driver. Privacy: questa pagina non mostra email, telefono o dati sensibili.
          </p>
          <Link to="/become-driver" className="text-xs font-semibold text-dapnow-blue hover:underline mt-2 inline-block">
            Driver privati: servizio in arrivo. Scopri di più →
          </Link>
        </div>
      </article>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-white">
      <header className="px-4 sm:px-6 min-h-24 flex items-center justify-between gap-4 max-w-3xl mx-auto border-b border-dapnow-line">
        <Logo size="lg" />
        <Link to="/login" className="text-sm font-semibold text-dapnow-blue hover:underline">Accedi</Link>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">{children}</main>
      <footer className="max-w-3xl mx-auto px-4 sm:px-6 py-6 text-xs text-dapnow-muted text-center">
        © {new Date().getFullYear()} DapNow — Consegne locali in giornata · Ticino e Grigioni italiano.
      </footer>
    </div>
  );
}

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-2xl border border-dapnow-line p-3 text-center" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-dapnow-muted">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold tracking-tight">{value}</p>
      {sub && <p className="text-[10px] text-dapnow-muted mt-0.5">{sub}</p>}
    </div>
  );
}
